import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isFreeEmail } from 'free-email-domains-list';

/** Corporate domains (and their subdomains) that are always accepted. */
const ALLOWED_CORPORATE_EMAIL_DOMAINS = [
  'comcast.com',
  'cable.comcast.com',
  'xfinity.com',
  'xfinitymail.com',
  'nbcuni.com',
  'nbc.com',
  'nbcuniversal.com',
  'spa.com',
];

const PUBLIC_EMAIL_DOMAIN_ERROR =
  'Please use a company email address (no public domains).';

const isDomainOrSubdomain = (domain: string, base: string): boolean =>
  domain === base || domain.endsWith(`.${base}`);

/**
 * True when the email uses a public/free provider (Gmail, Yahoo, …).
 * Allowlisted corporate domains and incomplete addresses return false.
 */
function isPublicEmail(value: unknown): boolean {
  const email = String(value ?? '')
    .trim()
    .toLowerCase();
  const at = email.lastIndexOf('@');
  if (at < 0 || at === email.length - 1) return false;

  const domain = email.slice(at + 1);
  if (
    ALLOWED_CORPORATE_EMAIL_DOMAINS.some((d) => isDomainOrSubdomain(domain, d))
  ) {
    return false;
  }

  return isFreeEmail(email);
}

/**
 * Rejects public email domains with `{ publicEmailDomain: true }`.
 *
 * @param skip Evaluated on every validation run; return true to bypass the
 *   check (e.g. internal-only users).
 */
function nonPublicEmailValidator(skip?: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (skip?.()) return null;
    return isPublicEmail(control.value) ? { publicEmailDomain: true } : null;
  };
}

export {
  ALLOWED_CORPORATE_EMAIL_DOMAINS,
  isPublicEmail,
  nonPublicEmailValidator,
  PUBLIC_EMAIL_DOMAIN_ERROR,
};
